// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/serialize";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Accordion, Checkbox, ShowMoreButton, Spacer } from "@aviary";
import { useTaxonomiesQuery } from "@shared/data/queries/TaxonomiesQuery/Taxonomies.query";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { useIsFullscriptJsVar } from "@shared/reactiveVar/useIsFullscriptJs/useIsFullscriptJs.store";

import type { TaxonomyFilter } from "../../CatalogSearch";
import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./PersonalCare.styles";

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  title?: string;
  customAccordionStyle?: SerializedStyles[] | SerializedStyles;
}

const PersonalCare = ({ isBold, logFilterEvent, title, customAccordionStyle }: Props) => {
  const { t } = useTranslation();
  const [isEurekaMegamenuEnabled] = useFlippers("eureka_patient_megamenu");
  const { activeTaxonomies, setActiveTaxonomies } = useCatalogSearch();
  const isFullscriptJs = useIsFullscriptJsVar();
  const { isUnauthenticated, isPatient } = useSharedGlobalConfig();
  const [showAll, setShowAll] = useState(false);

  const { data, loading } = useTaxonomiesQuery({
    variables: { levels: 1, topLevelName: "Personal Care & Healthy Living" },
  });

  const showLessCutoff = 5;
  const personalCareCategories = data?.viewer?.taxonomies;

  if (
    loading ||
    !personalCareCategories ||
    isFullscriptJs ||
    isUnauthenticated ||
    (isPatient && !isEurekaMegamenuEnabled)
  ) {
    return null;
  }

  const isFormActive = (id: string): boolean =>
    activeTaxonomies?.some(activeTaxonomy => activeTaxonomy.id === id);

  const handleFormSelect = (personalCareItemSelected: TaxonomyFilter) => {
    const distanceFromTop = window?.scrollY;
    if (isFormActive(personalCareItemSelected.id)) {
      setActiveTaxonomies(
        activeTaxonomies?.filter(taxonomy => taxonomy.id !== personalCareItemSelected.id)
      );
    } else {
      logFilterEvent(personalCareItemSelected.name, FilterType.PERSONAL_CARE);
      const taxonomy = {
        id: personalCareItemSelected.id,
        name: personalCareItemSelected.name,
      };
      setActiveTaxonomies([...(activeTaxonomies ?? []), taxonomy]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderForms = () =>
    personalCareCategories
      ?.filter(category => category?.name)
      .sort((a, b) => a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase()))
      .map((taxonomy, index) => {
        if (showAll || index < showLessCutoff) {
          return (
            <span key={taxonomy.id} css={styles.checkbox}>
              <Checkbox
                isFullWidth
                checked={isFormActive(taxonomy.id)}
                onChange={() => handleFormSelect(taxonomy)}
              >
                {taxonomy.name}
              </Checkbox>
            </span>
          );
        }

        return null;
      });

  const activePersonalCareCount = activeTaxonomies?.length;

  return (
    <Accordion
      css={customAccordionStyle}
      coreTriggerContent={
        <FilterTitle
          title={title || t(l.sharedCatalog.filter.PersonalCare)}
          count={activePersonalCareCount}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      <div css={styles.checkboxList}>
        {renderForms()}
        <Spacer height="half" />
        <ShowMoreButton onClick={() => setShowAll(!showAll)} isExpanded={showAll} />
      </div>
    </Accordion>
  );
};

export { PersonalCare };
